import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@dsx/react';
import type { IAsynchronousAction } from '../../../appstate';
import {
	Control,
	Field,
	FieldBody,
	FieldLabel,
	Label,
} from '../../../components/Bulma';
import Modal from '../../../components/DSX/Modal';
import { constants } from '../../../constants';
import { key } from '../../../messages';

export interface ILoginProps {
	signingIn: IAsynchronousAction;
	unknownError: boolean;
	signIn: (username: string, password: string) => void;
}

export interface ILoginState {
	username: string;
	password: string;
}

export default class SignInModule extends React.Component<
	ILoginProps,
	ILoginState
> {
	private usernameField: HTMLInputElement;
	private passwordField: HTMLInputElement;

	constructor(props) {
		super(props);
		this.state = {
			password: '',
			username: '',
		};
		this.onPasswordChange = this.onPasswordChange.bind(this);
		this.onUsernameChange = this.onUsernameChange.bind(this);
	}

	public componentDidMount() {
		if (!navigator.cookieEnabled) {
			this.usernameField.disabled = true;
			this.passwordField.disabled = true;
		}
		if (!this.usernameField.disabled) {
			this.usernameField.focus();
		}
	}

	public signIn = (): void => {
		const { signIn } = this.props;
		signIn(this.state.username, this.state.password);
	};

	public handleKeyUp = (event) => {
		const { keyCodes } = constants;

		if (event.which === keyCodes.enter) {
			this.signIn();
		}
	};

	public onUsernameChange = (event) => {
		this.setState({
			password: this.state.password,
			username: event.target.value,
		});
	};

	public onPasswordChange = (event) => {
		this.setState({
			username: this.state.username,
			password: event.target.value,
		});
	};

	public render() {
		const { signingIn, unknownError } = this.props;

		let error;
		if (signingIn.error) {
			error = (
				<span
					className="authentication-failure"
					data-test-id="authentication-failure"
				>
					<FormattedMessage
						id={key((msg) => msg.containers.login.signInModule.signInError)}
					/>
				</span>
			);
		}

		if (unknownError) {
			error = (
				<span
					className="authentication-failure"
					data-test-id="unknown-authentication-failure"
				>
					<FormattedMessage
						id={key((msg) => msg.containers.login.signInModule.unknownError)}
					/>
				</span>
			);
		}

		const loginNoticeTerms = (
			<FormattedMessage
				id={key((msg) => msg.privacyMessages.loginNoticeTerms)}
			/>
		);
		const terms = (
			<a
				href="https://www.directsupply.com/legal/terms-of-use/"
				rel="noreferrer"
				target="_blank"
			>
				<FormattedMessage id={key((msg) => msg.privacyMessages.termsOfUse)} />
			</a>
		);
		const loginNoticePrivacy = (
			<FormattedMessage
				id={key((msg) => msg.privacyMessages.loginNoticePrivacy)}
			/>
		);
		const privacy = (
			<a
				href="https://www.directsupply.com/legal/privacy-policy"
				rel="noreferrer"
				target="_blank"
			>
				<FormattedMessage
					id={key((msg) => msg.privacyMessages.privacyPolicy)}
				/>
			</a>
		);

		const loginNotice = (
			<p className="container text-center-xs text-muted">
				{loginNoticeTerms}
				{terms} {loginNoticePrivacy}
				{privacy}.
			</p>
		);

		const bodyText = (
			<span>
				<h2>
					<FormattedMessage
						id={key(
							(o) =>
								o.containers.login.signInModule.cookiesDisabled
									.cookiesDisabledBody
						)}
					/>
				</h2>
				<p>
					<FormattedMessage
						id={key(
							(o) =>
								o.containers.login.signInModule.cookiesDisabled
									.cookiesDisabledListHeader
						)}
					/>
				</p>
				<ul>
					<li>
						<a
							target="_blank"
							href="https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies"
							rel="noreferrer"
						>
							<FormattedMessage
								id={key(
									(o) =>
										o.containers.login.signInModule.cookiesDisabled
											.cookiesDisabledIELabel
								)}
							/>
						</a>
					</li>
					<li>
						<a
							target="_blank"
							href="https://support.google.com/chrome/answer/95647"
							rel="noreferrer"
						>
							<FormattedMessage
								id={key(
									(o) =>
										o.containers.login.signInModule.cookiesDisabled
											.cookiesDisabledChromeLabel
								)}
							/>
						</a>
					</li>
					<li>
						<a
							target="_blank"
							href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences"
							rel="noreferrer"
						>
							<FormattedMessage
								id={key(
									(o) =>
										o.containers.login.signInModule.cookiesDisabled
											.cookiesDisabledFirefoxLabel
								)}
							/>
						</a>
					</li>
				</ul>
			</span>
		);
		return (
			<div className="sign-in-module">
				<Modal
					show={!navigator.cookieEnabled}
					onCancel={() => {
						/* nothing */
					}}
					formattedStringHeader={
						<FormattedMessage
							id={key(
								(o) =>
									o.containers.login.signInModule.cookiesDisabled
										.cookiesDisabledHeader
							)}
						/>
					}
					bodyInput={bodyText}
					footerButtons=""
				/>
				<div className="card">
					<div className="wrapper">
						<div className="banner" />
						<form onSubmit={this.signIn} onKeyUp={this.handleKeyUp}>
							<Field isHorizontal={true}>
								<FieldLabel isNormal={true}>
									<Label>
										<FormattedMessage
											id={key(
												(msg) => msg.containers.login.signInModule.username
											)}
										/>
									</Label>
								</FieldLabel>
								<FieldBody>
									<Field>
										<Control>
											<input
												id="login-username"
												name="login-username"
												data-test-id="login-username"
												className="input"
												maxLength={64}
												value={this.state.username}
												onChange={this.onUsernameChange}
												ref={(input) => (this.usernameField = input)}
												type="text"
											/>
										</Control>
									</Field>
								</FieldBody>
							</Field>
							<Field isHorizontal={true}>
								<FieldLabel isNormal={true}>
									<Label>
										<FormattedMessage
											id={key(
												(msg) => msg.containers.login.signInModule.password
											)}
										/>
									</Label>
								</FieldLabel>
								<FieldBody>
									<Field>
										<Control>
											<input
												id="login-password"
												name="login-password"
												data-test-id="login-password"
												className="input"
												maxLength={64}
												value={this.state.password}
												onChange={this.onPasswordChange}
												ref={(input) => (this.passwordField = input)}
												type="password"
											/>
										</Control>
									</Field>
								</FieldBody>
							</Field>
							<div className="text-center-xs">{error}</div>
							<div className="text-center-xs">{loginNotice}</div>
							<div className="actions">
								<div className="sign-in-button" data-test-id="login-submit">
									<Button
										tag="login-submit"
										variant="primary"
										loadingState={signingIn.inProcess ? 'loading' : 'initial'}
										onClick={this.signIn}
										disabled={!navigator.cookieEnabled || signingIn.inProcess}
									>
										Sign In
									</Button>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		);
	}
}
